import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { useAuth } from "../contexts/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import { toast } from "sonner";
import {
  Link,
  Plus,
  Copy,
  Loader2,
  TrendingUp,
  Users,
  DollarSign,
  Tag,
  LogOut,
  BarChart3,
  Wallet,
  ArrowUpRight,
  History,
  CheckCircle,
  Clock,
  XCircle,
  CreditCard,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ReferralsPage() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [codes, setCodes] = useState([]);
  const [earnings, setEarnings] = useState(null);
  const [commissions, setCommissions] = useState([]);
  const [payouts, setPayouts] = useState([]);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showWithdrawModal, setShowWithdrawModal] = useState(false);
  const [creating, setCreating] = useState(false);
  const [withdrawing, setWithdrawing] = useState(false);
  const [activeTab, setActiveTab] = useState("codes");
  const [newCode, setNewCode] = useState({
    code: "",
    discount_percent: 10,
    valid_days: 90
  });

  useEffect(() => {
    // Check if user is sales director or platform admin
    if (user && !["sales_director", "platform_admin"].includes(user.role)) {
      toast.error("Access denied");
      navigate("/");
      return;
    }
    fetchData();
  }, [user, navigate]);

  const fetchData = async () => {
    try {
      const [statsRes, codesRes, earningsRes, commissionsRes, payoutsRes] = await Promise.all([
        axios.get(`${API}/referrals/stats`),
        axios.get(`${API}/referrals/codes`),
        axios.get(`${API}/referrals/earnings`),
        axios.get(`${API}/referrals/commissions`),
        axios.get(`${API}/referrals/payouts`)
      ]);
      setStats(statsRes.data);
      setCodes(codesRes.data.codes || []);
      setEarnings(earningsRes.data);
      setCommissions(commissionsRes.data || []);
      setPayouts(payoutsRes.data || []);
    } catch (error) {
      toast.error("Failed to load referral data");
    } finally {
      setLoading(false);
    }
  };

  const handleCreateCode = async (e) => {
    e.preventDefault();
    setCreating(true);
    try {
      const response = await axios.post(`${API}/referrals/codes`, newCode);
      toast.success(`Referral code ${response.data.code} created!`);
      setShowCreateModal(false);
      setNewCode({ code: "", discount_percent: 10, valid_days: 90 });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to create code");
    } finally {
      setCreating(false);
    }
  };

  const handleWithdraw = async () => {
    setWithdrawing(true);
    try {
      const response = await axios.post(`${API}/referrals/withdraw`, {
        payout_method: "bank_transfer"
      });
      toast.success(response.data.message);
      setShowWithdrawModal(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to request withdrawal");
    } finally {
      setWithdrawing(false);
    }
  };

  const copyCode = (code) => {
    navigator.clipboard.writeText(code);
    toast.success("Code copied to clipboard");
  };

  const copySignupLink = (code) => {
    const link = `${window.location.origin}/signup?ref=${code}`;
    navigator.clipboard.writeText(link);
    toast.success("Signup link copied to clipboard");
  };

  const getPayoutStatusBadge = (status) => {
    switch (status) {
      case "completed":
        return <span className="flex items-center gap-1 px-2 py-1 bg-emerald-500/20 text-emerald-300 text-xs rounded-full"><CheckCircle className="w-3 h-3" /> Completed</span>;
      case "processing":
        return <span className="flex items-center gap-1 px-2 py-1 bg-blue-500/20 text-blue-300 text-xs rounded-full"><Loader2 className="w-3 h-3 animate-spin" /> Processing</span>;
      case "requested":
        return <span className="flex items-center gap-1 px-2 py-1 bg-amber-500/20 text-amber-300 text-xs rounded-full"><Clock className="w-3 h-3" /> Requested</span>;
      case "failed":
      case "cancelled":
        return <span className="flex items-center gap-1 px-2 py-1 bg-red-500/20 text-red-300 text-xs rounded-full"><XCircle className="w-3 h-3" /> {status}</span>;
      default:
        return <span className="px-2 py-1 bg-stone-500/20 text-stone-300 text-xs rounded-full">{status}</span>;
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-indigo-900 to-purple-900 flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-white animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-900 to-purple-900">
      {/* Header */}
      <header className="bg-white/10 backdrop-blur-sm border-b border-white/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-white/20 rounded-lg flex items-center justify-center">
                <Link className="w-6 h-6 text-white" />
              </div>
              <div>
                <h1 className="text-white font-semibold">MyMeetingsPal</h1>
                <p className="text-xs text-indigo-200">Referral Dashboard</p>
              </div>
            </div>
            
            <div className="flex items-center gap-4">
              <div className="text-right">
                <p className="text-sm text-white">{user?.name}</p>
                <p className="text-xs text-indigo-200">{user?.role === "platform_admin" ? "Platform Admin" : "Sales Director"}</p>
              </div>
              <Button
                variant="ghost"
                size="sm"
                onClick={logout}
                className="text-indigo-200 hover:text-white"
              >
                <LogOut className="w-4 h-4" />
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Earnings Card - Prominent */}
        <div className="bg-gradient-to-r from-emerald-600 to-teal-600 rounded-xl p-6 mb-8 shadow-lg">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-6">
            <div>
              <div className="flex items-center gap-2 mb-2">
                <Wallet className="w-6 h-6 text-emerald-100" />
                <h2 className="text-lg font-semibold text-white">Your Earnings</h2>
              </div>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
                <div>
                  <p className="text-emerald-100 text-sm">Available Balance</p>
                  <p className="text-3xl font-bold text-white">${earnings?.available_balance?.toFixed(2) || '0.00'}</p>
                </div>
                <div>
                  <p className="text-emerald-100 text-sm">Pending</p>
                  <p className="text-xl font-semibold text-white/80">${earnings?.pending_balance?.toFixed(2) || '0.00'}</p>
                </div>
                <div>
                  <p className="text-emerald-100 text-sm">Total Earned</p>
                  <p className="text-xl font-semibold text-white/80">${earnings?.total_earned?.toFixed(2) || '0.00'}</p>
                </div>
                <div>
                  <p className="text-emerald-100 text-sm">Total Withdrawn</p>
                  <p className="text-xl font-semibold text-white/80">${earnings?.total_withdrawn?.toFixed(2) || '0.00'}</p>
                </div>
              </div>
              <p className="text-emerald-100 text-xs mt-3">
                Commission Rate: {earnings?.commission_rate_display || '10%'} recurring on all payments from referred customers
              </p>
            </div>
            <div className="flex flex-col gap-2">
              <Button
                onClick={() => setShowWithdrawModal(true)}
                disabled={!earnings?.can_withdraw}
                className="bg-white text-emerald-700 hover:bg-emerald-50 font-semibold px-6"
                data-testid="withdraw-btn"
              >
                <ArrowUpRight className="w-4 h-4 mr-2" />
                Withdraw
              </Button>
              {!earnings?.can_withdraw && (
                <p className="text-xs text-emerald-100 text-center">
                  Min. ${earnings?.minimum_payout?.toFixed(0) || '300'} required
                </p>
              )}
            </div>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
          <div className="bg-white/10 backdrop-blur-sm border border-white/20 rounded-xl p-6">
            <div className="flex items-center gap-3 mb-2">
              <Tag className="w-5 h-5 text-indigo-300" />
              <span className="text-indigo-200 text-sm">Referral Codes</span>
            </div>
            <p className="text-3xl font-bold text-white">{stats?.total_referrals || 0}</p>
          </div>
          
          <div className="bg-white/10 backdrop-blur-sm border border-white/20 rounded-xl p-6">
            <div className="flex items-center gap-3 mb-2">
              <Users className="w-5 h-5 text-emerald-300" />
              <span className="text-indigo-200 text-sm">Total Signups</span>
            </div>
            <p className="text-3xl font-bold text-white">{stats?.total_signups || 0}</p>
          </div>
          
          <div className="bg-white/10 backdrop-blur-sm border border-white/20 rounded-xl p-6">
            <div className="flex items-center gap-3 mb-2">
              <DollarSign className="w-5 h-5 text-amber-300" />
              <span className="text-indigo-200 text-sm">Revenue Generated</span>
            </div>
            <p className="text-3xl font-bold text-white">${stats?.total_revenue?.toFixed(2) || '0.00'}</p>
          </div>
          
          <div className="bg-white/10 backdrop-blur-sm border border-white/20 rounded-xl p-6">
            <div className="flex items-center gap-3 mb-2">
              <TrendingUp className="w-5 h-5 text-purple-300" />
              <span className="text-indigo-200 text-sm">Active Codes</span>
            </div>
            <p className="text-3xl font-bold text-white">{stats?.active_codes || 0}</p>
          </div>
        </div>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="bg-white/10 border border-white/20 mb-6">
            <TabsTrigger value="codes" className="text-indigo-200 data-[state=active]:bg-white/20 data-[state=active]:text-white">
              <Tag className="w-4 h-4 mr-2" />
              Referral Codes
            </TabsTrigger>
            <TabsTrigger value="commissions" className="text-indigo-200 data-[state=active]:bg-white/20 data-[state=active]:text-white">
              <DollarSign className="w-4 h-4 mr-2" />
              Commissions
            </TabsTrigger>
            <TabsTrigger value="payouts" className="text-indigo-200 data-[state=active]:bg-white/20 data-[state=active]:text-white">
              <History className="w-4 h-4 mr-2" />
              Payouts
            </TabsTrigger>
          </TabsList>

          {/* Referral Codes Tab */}
          <TabsContent value="codes">
            <div className="bg-white/10 backdrop-blur-sm border border-white/20 rounded-xl">
              <div className="p-4 border-b border-white/10 flex items-center justify-between">
                <h3 className="text-lg font-semibold text-white">Your Referral Codes</h3>
                <Button
                  size="sm"
                  onClick={() => setShowCreateModal(true)}
                  className="bg-indigo-500 hover:bg-indigo-600"
                  data-testid="create-code-btn"
                >
                  <Plus className="w-4 h-4 mr-1" />
                  Create Code
                </Button>
              </div>
              
              <div className="divide-y divide-white/10">
                {codes.length > 0 ? (
                  codes.map((code) => (
                    <div key={code.id} className="p-4 flex items-center justify-between">
                      <div className="flex items-center gap-4">
                        <div className="w-12 h-12 bg-gradient-to-br from-indigo-500 to-purple-500 rounded-lg flex items-center justify-center">
                          <Tag className="w-6 h-6 text-white" />
                        </div>
                        <div>
                          <div className="flex items-center gap-2">
                            <span className="text-white font-mono text-lg font-semibold">{code.code}</span>
                            <button
                              onClick={() => copyCode(code.code)}
                              className="p-1 hover:bg-white/10 rounded"
                              title="Copy code"
                            >
                              <Copy className="w-4 h-4 text-indigo-300" />
                            </button>
                          </div>
                          <p className="text-sm text-indigo-200">
                            {code.discount_percent}% off • Valid until {new Date(code.valid_until).toLocaleDateString()}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-6">
                        <div className="text-right">
                          <p className="text-white font-semibold">{code.total_signups} signups</p>
                          <p className="text-sm text-indigo-200">${code.total_revenue?.toFixed(2) || '0.00'} revenue</p>
                        </div>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => copySignupLink(code.code)}
                          className="border-indigo-400 text-indigo-200 hover:bg-white/10"
                        >
                          <Link className="w-4 h-4 mr-1" />
                          Copy Link
                        </Button>
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="p-8 text-center">
                    <Tag className="w-12 h-12 text-indigo-400 mx-auto mb-3" />
                    <p className="text-indigo-200">No referral codes yet</p>
                    <p className="text-sm text-indigo-300 mt-1">Create your first code to start earning</p>
                  </div>
                )}
              </div>
            </div>

            {/* Recent Signups */}
            {stats?.attributions?.length > 0 && (
              <div className="mt-8 bg-white/10 backdrop-blur-sm border border-white/20 rounded-xl">
                <div className="p-4 border-b border-white/10">
                  <h3 className="text-lg font-semibold text-white flex items-center gap-2">
                    <BarChart3 className="w-5 h-5" />
                    Recent Signups
                  </h3>
                </div>
                <div className="divide-y divide-white/10">
                  {stats.attributions.map((attr, idx) => (
                    <div key={idx} className="p-4 flex items-center justify-between">
                      <div>
                        <p className="text-white font-medium">{attr.name}</p>
                        <p className="text-sm text-indigo-200">
                          Code: {attr.referral_attribution?.referral_code} • {new Date(attr.created_at).toLocaleDateString()}
                        </p>
                      </div>
                      <div className="text-right">
                        <span className={`px-2 py-1 text-xs rounded-full ${
                          attr.plan_tier === 'professional' ? 'bg-purple-500/20 text-purple-300' :
                          'bg-indigo-500/20 text-indigo-300'
                        }`}>
                          {attr.plan_tier}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </TabsContent>

          {/* Commissions Tab */}
          <TabsContent value="commissions">
            <div className="bg-white/10 backdrop-blur-sm border border-white/20 rounded-xl">
              <div className="p-4 border-b border-white/10">
                <h3 className="text-lg font-semibold text-white">Commission History</h3>
              </div>
              <div className="divide-y divide-white/10">
                {commissions.length > 0 ? (
                  commissions.map((commission) => (
                    <div key={commission.id} className="p-4 flex items-center justify-between">
                      <div className="flex items-center gap-4">
                        <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                          commission.status === 'available' ? 'bg-emerald-500/20' :
                          commission.status === 'withdrawn' ? 'bg-blue-500/20' :
                          'bg-amber-500/20'
                        }`}>
                          <DollarSign className={`w-5 h-5 ${
                            commission.status === 'available' ? 'text-emerald-400' :
                            commission.status === 'withdrawn' ? 'text-blue-400' :
                            'text-amber-400'
                          }`} />
                        </div>
                        <div>
                          <p className="text-white font-medium">{commission.entity_name}</p>
                          <p className="text-sm text-indigo-200">
                            Code: {commission.referral_code} • {new Date(commission.created_at).toLocaleDateString()}
                          </p>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="text-white font-semibold">+${commission.commission_amount.toFixed(2)}</p>
                        <p className="text-xs text-indigo-300">
                          {(commission.commission_rate * 100).toFixed(0)}% of ${commission.payment_amount.toFixed(2)}
                        </p>
                        <span className={`inline-block mt-1 px-2 py-0.5 text-xs rounded-full ${
                          commission.status === 'available' ? 'bg-emerald-500/20 text-emerald-300' :
                          commission.status === 'withdrawn' ? 'bg-blue-500/20 text-blue-300' :
                          'bg-amber-500/20 text-amber-300'
                        }`}>
                          {commission.status}
                        </span>
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="p-8 text-center">
                    <DollarSign className="w-12 h-12 text-indigo-400 mx-auto mb-3" />
                    <p className="text-indigo-200">No commissions yet</p>
                    <p className="text-sm text-indigo-300 mt-1">Commissions appear when referred customers make payments</p>
                  </div>
                )}
              </div>
            </div>
          </TabsContent>

          {/* Payouts Tab */}
          <TabsContent value="payouts">
            <div className="bg-white/10 backdrop-blur-sm border border-white/20 rounded-xl">
              <div className="p-4 border-b border-white/10">
                <h3 className="text-lg font-semibold text-white">Payout History</h3>
              </div>
              <div className="divide-y divide-white/10">
                {payouts.length > 0 ? (
                  payouts.map((payout) => (
                    <div key={payout.id} className="p-4 flex items-center justify-between">
                      <div className="flex items-center gap-4">
                        <div className="w-10 h-10 bg-white/10 rounded-lg flex items-center justify-center">
                          <CreditCard className="w-5 h-5 text-indigo-300" />
                        </div>
                        <div>
                          <p className="text-white font-medium">Withdrawal Request</p>
                          <p className="text-sm text-indigo-200">
                            {new Date(payout.requested_at).toLocaleDateString()} • {payout.payout_method}
                          </p>
                          {payout.processed_at && (
                            <p className="text-xs text-indigo-300">
                              Processed: {new Date(payout.processed_at).toLocaleDateString()}
                            </p>
                          )}
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="text-white font-semibold text-lg">${payout.amount.toFixed(2)}</p>
                        {getPayoutStatusBadge(payout.status)}
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="p-8 text-center">
                    <History className="w-12 h-12 text-indigo-400 mx-auto mb-3" />
                    <p className="text-indigo-200">No payouts yet</p>
                    <p className="text-sm text-indigo-300 mt-1">Request a withdrawal when you reach the minimum threshold</p>
                  </div>
                )}
              </div>
            </div>
          </TabsContent>
        </Tabs>
      </main>

      {/* Create Code Modal */}
      <Dialog open={showCreateModal} onOpenChange={setShowCreateModal}>
        <DialogContent className="bg-indigo-900 border-indigo-700 text-white">
          <DialogHeader>
            <DialogTitle>Create Referral Code</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleCreateCode} className="space-y-4 mt-4">
            <div>
              <Label className="text-indigo-200">Code (optional - auto-generated if empty)</Label>
              <Input
                value={newCode.code}
                onChange={(e) => setNewCode({ ...newCode, code: e.target.value.toUpperCase() })}
                placeholder="e.g., JOHNSMITH"
                className="bg-indigo-800 border-indigo-600 text-white font-mono"
              />
            </div>
            <div>
              <Label className="text-indigo-200">Discount Percent</Label>
              <Input
                type="number"
                min="0"
                max="100"
                value={newCode.discount_percent}
                onChange={(e) => setNewCode({ ...newCode, discount_percent: parseInt(e.target.value) })}
                className="bg-indigo-800 border-indigo-600 text-white"
              />
            </div>
            <div>
              <Label className="text-indigo-200">Valid for (days)</Label>
              <Input
                type="number"
                min="1"
                value={newCode.valid_days}
                onChange={(e) => setNewCode({ ...newCode, valid_days: parseInt(e.target.value) })}
                className="bg-indigo-800 border-indigo-600 text-white"
              />
            </div>
            <Button type="submit" disabled={creating} className="w-full bg-indigo-500 hover:bg-indigo-600">
              {creating ? <Loader2 className="w-4 h-4 animate-spin" /> : "Create Code"}
            </Button>
          </form>
        </DialogContent>
      </Dialog>

      {/* Withdraw Modal */}
      <Dialog open={showWithdrawModal} onOpenChange={setShowWithdrawModal}>
        <DialogContent className="bg-indigo-900 border-indigo-700 text-white">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Wallet className="w-5 h-5" />
              Request Withdrawal
            </DialogTitle>
          </DialogHeader>
          <div className="mt-4 space-y-4">
            <div className="bg-emerald-500/20 rounded-lg p-4">
              <p className="text-emerald-100 text-sm">Available Balance</p>
              <p className="text-3xl font-bold text-white">${earnings?.available_balance?.toFixed(2) || '0.00'}</p>
            </div>
            
            <div className="bg-indigo-800/50 rounded-lg p-4">
              <p className="text-indigo-200 text-sm mb-2">Payout Method</p>
              <div className="flex items-center gap-3 p-3 bg-indigo-700/50 rounded-lg border border-indigo-600">
                <CreditCard className="w-5 h-5 text-indigo-300" />
                <div>
                  <p className="text-white font-medium">Bank Transfer</p>
                  <p className="text-xs text-indigo-300">3-5 business days</p>
                </div>
              </div>
            </div>
            
            <p className="text-xs text-indigo-300">
              By requesting a withdrawal, your full available balance of ${earnings?.available_balance?.toFixed(2)} will be processed. 
              Our team will contact you to confirm your bank details.
            </p>
            
            <Button 
              onClick={handleWithdraw} 
              disabled={withdrawing || !earnings?.can_withdraw}
              className="w-full bg-emerald-600 hover:bg-emerald-700"
            >
              {withdrawing ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <>
                  <ArrowUpRight className="w-4 h-4 mr-2" />
                  Withdraw ${earnings?.available_balance?.toFixed(2)}
                </>
              )}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
